import React, { useEffect, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import "./TimePeriodPicker.css";
import { useTimePeriodContext } from "../../contexts/TimePeriodContext";
import { useSearchParams } from "react-router-dom";

interface TimePeriodPickerProps {
  variant?: "default" | "finance";
}

export const TimePeriodPicker = ({ variant = "default" }: TimePeriodPickerProps) => {
  const { periods, selectedIndex, setSelectedIndex } = useTimePeriodContext();
  const [searchParams] = useSearchParams();

  // Sync from URL params
  useEffect(() => {
    const month = Number(searchParams.get("month"));
    const year = Number(searchParams.get("year"));

    if (Number.isInteger(month) && Number.isInteger(year) && month >= 1 && month <= 12) {
      const index = periods.findIndex((p) => p.Month === month && p.Year === year);
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [searchParams, periods, setSelectedIndex]);

  const clampIndex = useCallback(
    (idx: number) => Math.max(0, Math.min(periods.length - 1, idx)),
    [periods.length],
  );

  const handleWheel = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaY) < 10) return;
    const direction = e.deltaY > 0 ? 1 : -1;
    setSelectedIndex(clampIndex(selectedIndex + direction));
  };

  const goPrev = () => setSelectedIndex(clampIndex(selectedIndex - 1));
  const goNext = () => setSelectedIndex(clampIndex(selectedIndex + 1));

  const current = periods[selectedIndex];
  const prevPeriod = periods[selectedIndex - 1];
  const nextPeriod = periods[selectedIndex + 1];

  return (
    <div
      className={`timeperiod-slider timeperiod-slider--${variant}`}
      role="group"
      aria-label="Select time period"
    >
      <button
        type="button"
        className="timeperiod-slider__nav timeperiod-slider__nav--down"
        onClick={goPrev}
        disabled={selectedIndex === 0}
        aria-label="Previous period"
      >
        <ChevronUp size={18} strokeWidth={2.5} />
      </button>

      <div className="timeperiod-slider__track" onWheel={handleWheel}>
        {prevPeriod && variant === "default" && (
          <div className="timeperiod-slider__ghost timeperiod-slider__ghost--prev">
            {String(prevPeriod.Month).padStart(2, "0")} / {prevPeriod.Year}
          </div>
        )}

        <div className="timeperiod-slider__current">
          {current ? `${String(current.Month).padStart(2, "0")} / ${current.Year}` : "—"}
        </div>

        {nextPeriod && variant === "default" && (
          <div className="timeperiod-slider__ghost timeperiod-slider__ghost--next">
            {String(nextPeriod.Month).padStart(2, "0")} / {nextPeriod.Year}
          </div>
        )}
      </div>

      <button
        type="button"
        className="timeperiod-slider__nav timeperiod-slider__nav--up"
        onClick={goNext}
        disabled={selectedIndex === periods.length - 1}
        aria-label="Next period"
      >
        <ChevronDown size={18} strokeWidth={2.5} />
      </button>
    </div>
  );
};
